import { useState } from "react";
import { Button, Input } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { AutoPlusIcon } from "@assets/icons";

export interface Header {
  key: string;
  value: string;
}

interface ApiHeadersProps {
  headers: Header[];
  setHeaders: React.Dispatch<React.SetStateAction<Header[]>>;
  error?: string;
}

const ApiHeaders = ({ headers = [], setHeaders, error }: ApiHeadersProps) => {
  console.log(headers);
  const [isHeaderEditing, setIsHeaderEditing] = useState(false);
  const [editingHeaderIndex, setEditingHeaderIndex] = useState<number | null>(null);
  const [headerDraft, setHeaderDraft] = useState<Header>({ key: "", value: "" });

  const handleHeaderSave = () => {
    if (!headerDraft.key) return;
    if (editingHeaderIndex !== null) {
      setHeaders((prev: Header[]) => {
        const updated = [...prev];
        updated[editingHeaderIndex as number] = headerDraft;
        return updated;
      });
    } else {
      setHeaders((prev: Header[]) => [...prev, headerDraft]);
    }
    setIsHeaderEditing(false);
    setEditingHeaderIndex(null);
    setHeaderDraft({ key: "", value: "" });
  };

  const handleHeaderEdit = (header: Header, idx: number) => {
    setIsHeaderEditing(true);
    setEditingHeaderIndex(idx);
    setHeaderDraft(header);
  };

  const handleHeaderDelete = (idx: number) => {
    setHeaders((prev: Header[]) => prev.filter((_: Header, i: number) => i !== idx));
  };

  const handleHeaderCancel = () => {
    setIsHeaderEditing(false);
    setEditingHeaderIndex(null);
    setHeaderDraft({ key: "", value: "" });
  };

  return (
    <Stack direction="column" className="al-gap-2">
      <Stack direction="row" className="al-justify-between al-items-center">
        <h3 className="al-text-sm al-font-medium">Headers</h3>
        {!isHeaderEditing && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setIsHeaderEditing(true);
              setEditingHeaderIndex(null);
              setHeaderDraft({ key: "", value: "" });
            }}
          >
            <AutoPlusIcon className="al-w-4 al-h-4 al-mr-2" />
            Add Header
          </Button>
        )}
      </Stack>
      <p className="al-text-sm al-text-gray-500">
        Add custom HTTP headers for your API integration.
      </p>
      {error && <p className="al-text-sm al-text-destructive">{error}</p>}
      {/* List headers */}
      {!isHeaderEditing && headers.length > 0 && (
        <div className="al-space-y-2">
          {headers.map((header, idx) => (
            <Stack key={header.key + idx} direction="row" className="al-gap-2 al-items-center">
              <span className="al-text-xs al-font-mono al-bg-gray-100 al-px-2 al-py-1 al-rounded">
                {header.key}: {header.value}
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => handleHeaderEdit(header, idx)}
              >
                Edit
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => handleHeaderDelete(idx)}
              >
                Delete
              </Button>
            </Stack>
          ))}
        </div>
      )}
      {/* Header form */}
      {isHeaderEditing && (
        <div className="al-bg-gray-50 al-p-3 al-rounded al-space-y-2">
          <Stack direction="row" className="al-gap-2">
            <Input
              placeholder="Header Key"
              value={headerDraft.key}
              onChange={e => setHeaderDraft(d => ({ ...d, key: e.target.value }))}
            />
            <Input
              placeholder="Header Value"
              value={headerDraft.value}
              onChange={e => setHeaderDraft(d => ({ ...d, value: e.target.value }))}
            />
          </Stack>
          <Stack direction="row" className="al-gap-2">
            <Button type="button" size="sm" onClick={handleHeaderSave}>
              Save
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={handleHeaderCancel}>
              Cancel
            </Button>
          </Stack>
        </div>
      )}
    </Stack>
  );
};

export default ApiHeaders;
